import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Navbar from '../../../../ReusableBlocks/Navbar';
import {
  PARTNER_PORTAL_NOT_AUTHORIZED,
  PARTNER_PORTAL_SUBMIT,
} from '../Constants/partnerRoutes';
import PageHeader from '../Components/shared/PageHeader';
import PartnerPagination from '../Components/shared/PartnerPagination';
import ProjectTable from '../Components/tables/ProjectTable';
import { usePartnerFilters } from '../Hooks/usePartnerFilters';
import { PARTNER_TABLE_PAGE_SIZE, usePartnerPagination } from '../Hooks/usePartnerPagination';
import { usePartnerProjects } from '../Hooks/usePartnerProjects';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import { usePartnerTableSortFilter } from '../Hooks/usePartnerTableSortFilter';
import PartnerLayout from '../Layout/PartnerLayout';
import ProjectDetailsPage from './ProjectDetailsPage';
import {
  applyPartnerTableColumnFilters,
  buildPartnerTableRegionFilterOptions,
  buildPartnerTableStatusFilterOptions,
  getMyProjectsColumnValue,
  getMyProjectsSortValue,
  MY_PROJECTS_FILTER_COLUMNS,
  MY_PROJECTS_TABLE_COLUMNS,
} from '../Utils/partnerTableSortFilterUtils';
import PartnerTableFiltersModal, {
  PartnerTableFiltersButton,
  PartnerTableResetFiltersButton,
} from '../Components/shared/PartnerTableFiltersModal';
import PartnerTableExportButton from '../Components/shared/PartnerTableExportButton';
import { exportMyProjectsCsv } from '../Utils/partnerProjectExportUtils';
import {
  countMyProjectsStatCards,
  MY_PROJECTS_STAT_CARD_FILTERS,
  MY_PROJECTS_STAT_FILTER_ALL,
} from '../Utils/partnerMyProjectsUtils';
import { resolveAssignedPaeFilterValue } from '../Utils/partnerUserUtils';

const MyProjectsPage = () => {
  const access = usePartnerRoleAccess();
  const currentUser = useSelector((state) => state.auth?.currentUser);
  const { projects, partnerAdoptionEngineers, loading, error, refreshProjects } = usePartnerProjects();
  const { filteredProjects, search, status, setSearch, setStatus } = usePartnerFilters(projects);

  const getColumnValue = useCallback(
    (project, columnKey) => getMyProjectsColumnValue(project, columnKey),
    [],
  );
  const getSortValue = useCallback(
    (project, columnKey) => getMyProjectsSortValue(project, columnKey),
    [],
  );

  // Same as PAE Queue: PAEs default to their assigned projects; choose All in Filters for everything.
  const defaultPaeFilter = useMemo(
    () => (
      access.canPerformPAEOperations
        ? resolveAssignedPaeFilterValue(currentUser, partnerAdoptionEngineers)
        : null
    ),
    [access.canPerformPAEOperations, currentUser, partnerAdoptionEngineers],
  );

  const initialColumnFilters = useMemo(
    () => (defaultPaeFilter ? { pae: defaultPaeFilter } : {}),
    [defaultPaeFilter],
  );

  const {
    columnFilters,
    applyColumnFilters,
    hasActiveColumnFilters,
    resetColumnFilters,
    sortKey,
    sortDirection,
    toggleSort,
    processedProjects,
    columnFiltersKey,
    sortKeyValue,
  } = usePartnerTableSortFilter(filteredProjects, {
    getColumnValue,
    getSortValue,
    initialColumnFilters,
  });

  // Stat tiles follow search + column filters (e.g. partner), but ignore the status tile
  // so Active / Sales cycle / etc. stay meaningful within the filtered set.
  const statsBaseProjects = useMemo(() => {
    const queryValue = search.trim().toLowerCase();
    const searchScoped = !queryValue
      ? projects
      : projects.filter((project) => (
        `${project.customerName || ''} ${project.partnerName || ''}`.toLowerCase().includes(queryValue)
      ));
    return applyPartnerTableColumnFilters(searchScoped, columnFilters, getColumnValue);
  }, [projects, search, columnFilters, getColumnValue]);

  const statCounts = useMemo(
    () => countMyProjectsStatCards(statsBaseProjects),
    [statsBaseProjects],
  );
  const totalPoolCount = projects.length;

  const hasActiveToolbarFilters = hasActiveColumnFilters
    || Boolean(search.trim())
    || status !== MY_PROJECTS_STAT_FILTER_ALL;

  const handleResetAllFilters = () => {
    setSearch('');
    setStatus(MY_PROJECTS_STAT_FILTER_ALL);
    resetColumnFilters();
  };

  const {
    paginatedItems: paginatedProjects,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    setCurrentPage,
  } = usePartnerPagination(
    processedProjects,
    PARTNER_TABLE_PAGE_SIZE,
    `${search}:${status}:${columnFiltersKey}:${sortKeyValue}`,
  );
  const showFilteredSubset = totalItems !== totalPoolCount;
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const statusFilterOptions = useMemo(
    () => buildPartnerTableStatusFilterOptions(projects, (project) => project.projectStatus),
    [projects],
  );

  const regionFilterOptions = useMemo(
    () => buildPartnerTableRegionFilterOptions(projects, getColumnValue),
    [projects, getColumnValue],
  );

  if (!access.canAccessPartnerPages) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  const isDetailView = Boolean(selectedProjectId);

  const handleViewProject = (projectId) => {
    setSelectedProjectId(projectId);
  };

  const handleBackToProjects = async () => {
    setSelectedProjectId(null);
    await refreshProjects();
  };

  const handleExport = () => {
    exportMyProjectsCsv(processedProjects);
  };

  const handleStatFilter = (filterValue) => {
    setStatus((previous) => (previous === filterValue ? MY_PROJECTS_STAT_FILTER_ALL : filterValue));
    setCurrentPage(1);
  };

  const statCards = [
    {
      key: 'total',
      label: 'Total projects',
      hint: 'All registered deliveries',
      value: statCounts.total,
      filterValue: MY_PROJECTS_STAT_FILTER_ALL,
      tone: 'total',
    },
    {
      key: 'active',
      label: 'Active',
      hint: 'Delivery in progress',
      value: statCounts.active,
      filterValue: MY_PROJECTS_STAT_CARD_FILTERS.ACTIVE,
      tone: 'active',
    },
    {
      key: 'sales-cycle',
      label: 'Sales cycle',
      hint: 'Not started',
      summaryLabel: 'Sales cycle',
      value: statCounts.salesCycle,
      filterValue: MY_PROJECTS_STAT_CARD_FILTERS.SALES_CYCLE,
      tone: 'sales',
    },
    {
      key: 'info-requested',
      label: 'Info requested',
      hint: 'Awaiting your response',
      value: statCounts.infoRequested,
      filterValue: MY_PROJECTS_STAT_CARD_FILTERS.INFO_REQUESTED,
      tone: 'info',
    },
    {
      key: 'completed',
      label: 'Completed',
      hint: 'Closed deliveries',
      value: statCounts.completed,
      filterValue: MY_PROJECTS_STAT_CARD_FILTERS.COMPLETED,
      tone: 'completed',
    },
  ];

  const activeStatCard = statCards.find(
    (card) => card.filterValue === status,
  );
  const activeStatSummary = activeStatCard?.summaryLabel || activeStatCard?.label;

  return (
    <>
      <Navbar activeTab="PARTNER_PORTAL" />
      <PartnerLayout access={access}>
        {isDetailView ? (
          <ProjectDetailsPage
            inline
            projectId={selectedProjectId}
            onBackToProjects={handleBackToProjects}
          />
        ) : (
          <div className="partner-my-projects-page">
            <PageHeader
              title="My Projects"
              description="Track new, active, and completed deliveries for your organization."
              rightContent={(
                <Link className="btn btn-primary partner-submit-new-btn" to={PARTNER_PORTAL_SUBMIT}>
                  Submit new project
                </Link>
              )}
            />
            {loading ? (
              <p className="partner-loading-text">Loading projects...</p>
            ) : (
              <>
                {error ? <p className="text-danger">{error}</p> : null}

                <div className="partner-my-stats-row">
                  {statCards.map((card) => {
                    const isActive = status === card.filterValue;
                    return (
                      <button
                        key={card.key}
                        type="button"
                        className={`partner-portal-stat-card partner-portal-stat-card--${card.tone} ${isActive ? 'is-active' : ''}`}
                        aria-pressed={isActive}
                        onClick={() => handleStatFilter(card.filterValue)}
                      >
                        <span className="partner-portal-stat-card-accent" aria-hidden="true" />
                        <span className="partner-portal-stat-card-body">
                          <span className="partner-portal-stat-card-top">
                            <span className="partner-portal-stat-card-value">{card.value}</span>
                            <span className="partner-portal-stat-card-label">{card.label}</span>
                          </span>
                          <span className="partner-portal-stat-card-hint">{card.hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <section className="partner-my-workspace" aria-label="Project list">
                  <PartnerTableFiltersModal
                    isOpen={isFiltersModalOpen}
                    title="My Projects Filters"
                    columns={MY_PROJECTS_FILTER_COLUMNS}
                    projects={filteredProjects}
                    appliedFilters={columnFilters}
                    getColumnValue={getColumnValue}
                    externalFilterOptions={{
                      pae: partnerAdoptionEngineers,
                      status: statusFilterOptions,
                      region: regionFilterOptions,
                    }}
                    onClose={() => setIsFiltersModalOpen(false)}
                    onApply={applyColumnFilters}
                  />

                  <div className="partner-my-table-panel">
                    <div className="partner-my-table-panel-head">
                      <div className="partner-my-table-panel-head-summary">
                        <p className="partner-my-project-count" aria-live="polite">
                          <span className="partner-my-project-count-number">
                            {totalPoolCount.toLocaleString()}
                          </span>
                          {' '}
                          <span className="partner-my-project-count-label">
                            total
                            {' '}
                            {totalPoolCount === 1 ? 'project' : 'projects'}
                          </span>
                          {showFilteredSubset ? (
                            <>
                              <span className="partner-my-project-count-sep" aria-hidden="true">·</span>
                              <span className="partner-my-project-count-number partner-my-project-count-number--filtered">
                                {totalItems.toLocaleString()}
                              </span>
                              {status !== MY_PROJECTS_STAT_FILTER_ALL && activeStatSummary ? (
                                <>
                                  <span className="partner-my-project-count-sep" aria-hidden="true">·</span>
                                  <span className="partner-my-project-count-page">{activeStatSummary}</span>
                                </>
                              ) : null}
                            </>
                          ) : null}
                          {totalPages > 1 ? (
                            <>
                              <span className="partner-my-project-count-sep" aria-hidden="true">·</span>
                              <span className="partner-my-project-count-page">
                                Page
                                {' '}
                                {currentPage}
                                {' '}
                                of
                                {' '}
                                {totalPages}
                              </span>
                            </>
                          ) : null}
                        </p>
                        {hasActiveToolbarFilters ? (
                          <span className="partner-my-filtered-badge">Filtered</span>
                        ) : null}
                      </div>
                      <div className="partner-my-table-panel-head-toolbar">
                        <label className="partner-my-search">
                          <span className="partner-my-search-icon" aria-hidden="true" />
                          <input
                            className="form-control partner-my-search-input"
                            placeholder="Search customer, company, products..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                          />
                        </label>
                        <div className="partner-my-toolbar-actions partner-my-filter-action-group">
                          <PartnerTableFiltersButton
                            onClick={() => setIsFiltersModalOpen(true)}
                            hasActiveFilters={hasActiveColumnFilters}
                          />
                          <PartnerTableResetFiltersButton
                            onClick={handleResetAllFilters}
                            disabled={!hasActiveToolbarFilters}
                          />
                          <PartnerTableExportButton
                            onClick={handleExport}
                            itemCount={processedProjects.length}
                          />
                        </div>
                      </div>
                    </div>

                    {paginatedProjects.length === 0 ? (
                      <div className="partner-my-empty">
                        <h3>No projects match your filters</h3>
                        <p>Try clearing filters or submit a new project to get started.</p>
                        <div className="partner-my-empty-actions">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleResetAllFilters}
                            disabled={!hasActiveToolbarFilters}
                          >
                            Reset filters
                          </button>
                          <Link className="btn btn-primary btn-sm" to={PARTNER_PORTAL_SUBMIT}>
                            Submit project
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <>
                        <ProjectTable
                          projects={paginatedProjects}
                          onViewProject={handleViewProject}
                          sortKey={sortKey}
                          sortDirection={sortDirection}
                          onSort={toggleSort}
                        />
                        <PartnerPagination
                          className="partner-pagination--my-panel"
                          currentPage={currentPage}
                          totalPages={totalPages}
                          totalItems={totalItems}
                          startIndex={startIndex}
                          endIndex={endIndex}
                          itemLabel="projects"
                          onPageChange={setCurrentPage}
                        />
                      </>
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </PartnerLayout>
    </>
  );
};

export default MyProjectsPage;
